import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import type { BulkAction } from '../../../components/TableActionBar';
import { EditableCell } from '../../../components/gridEdit/EditableCell';
import { useStagedEdits } from '../../../components/gridEdit/useStagedEdits';
import ordersService from '../../../services/orders.service';
import {
  buildPayload, duplicatedPros, keyPrefixForScope, keysForPros, rowHasStagedEdit,
  specFor, stagingKey, type GridFieldMap,
} from '../../searchResults/orderGridEdit';
import {
  draggableRowsFor, templateGridEditMetaFor, templateRowIsApproved,
} from '../templateGridEdit';
import {
  useAdditionalFieldColumns,
  type AdditionalFieldSelection,
} from '../../../hooks/useAdditionalFields';
import { useViewedPros } from '../../../contexts/ViewedProsContext';
import { deletedRowClass } from '../../../utils/deletedRow';
import type { TemplateRow, TemplateView, TemplateStatus } from '../../../services/template.service';

interface Props {
  rows: TemplateRow[];
  /** Server hit the result cap — show the "refine your search" banner. */
  capped: boolean;
  loading?: boolean;
  error?: string | null;

  view: TemplateView;
  onViewChange?: (view: TemplateView) => void;

  /** Which of the two categories is shown. Mutually exclusive by design — the old
   *  additive View Deleted / View Unapproved toggles could combine into "all 82,718
   *  templates", which is what forced a 10k cap onto the board. */
  status: TemplateStatus;
  onStatusChange?: (status: TemplateStatus) => void;
  /** Deleted templates can be in these rows — a custom search (always status-blind)
   *  or a filter the user widened with its Include Deleted toggle. Flips the note. */
  includesDeleted?: boolean;
  /** Whether the category choice applied. False for ANY search or filter, including a
   *  filter without Include Deleted — that still spans both approval states. Greys
   *  the picker out. Deliberately not the inverse of `includesDeleted`: a filter can
   *  override the category while still excluding deleted. */
  statusApplied?: boolean;

  /** Row click → open the template's detail page. */
  onOpenTemplate?: (row: TemplateRow) => void;
  /** Mass actions for the action bar, scoped to the rows CURRENTLY visible in this
   *  table (TanTable hands each action its filtered row originals at click time). The
   *  page owns them because it owns the drawer they open. */
  bulkActions?: BulkAction[];
  /** Whether the user may edit template fields (`template-edit-fields`) — the gate on
   *  inline grid editing, the same one /pro puts the whole editable surface behind. */
  canEdit?: boolean;
  /** Called after a successful inline save so the page can refetch — the grid shows
   *  stale values otherwise, and the saverules engine may have written more than the
   *  cells the user touched. */
  onEditsApplied?: () => void;
  /** The page's added-column selection. Owned by the PAGE because it is an input
   *  to the fetch, which the page runs; this grid only turns it into columns and
   *  hands the picker back. Keyed per view, like the layout. */
  fieldSelection: AdditionalFieldSelection;
}

const dash = <span className="text-slate-400 dark:text-slate-500">—</span>;

/** Plain text column with an em-dash fallback for empty values. */
function text(id: string, header: string, size?: number): ColumnDef<TemplateRow> {
  return {
    id,
    accessorFn: (r) => r[id],
    header,
    ...(size ? { size } : {}),
    cell: ({ getValue }) => {
      const v = getValue<TemplateRow[string]>();
      return v === null || v === undefined || v === '' ? dash : <span>{String(v)}</span>;
    },
  };
}

/** Right-aligned numeric column (fixed decimals); sorts numerically. */
function num(id: string, header: string, decimals: number): ColumnDef<TemplateRow> {
  return {
    id,
    accessorFn: (r) => r[id],
    header,
    cell: ({ getValue }) => {
      const v = getValue<TemplateRow[string]>();
      if (v === null || v === undefined || v === '') return dash;
      const n = typeof v === 'number' ? v : Number(v);
      const disp = Number.isFinite(n)
        ? n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        : String(v);
      return <span className="tabular-nums block text-right">{disp}</span>;
    },
  };
}

/** The template's Template # — styled as a link (the whole row navigates). */
const proColumn: ColumnDef<TemplateRow> = {
  id: 'pronumber',
  accessorFn: (r) => r.pronumber,
  header: 'Template #',
  size: 110,
  cell: ({ getValue }) => (
    <span className="font-semibold text-blue-600 dark:text-blue-400">{String(getValue() ?? '')}</span>
  ),
};

// ── View 1: Template level (one row per template) ──────────────────────────
const TEMPLATE_COLUMNS: ColumnDef<TemplateRow>[] = [
  proColumn,
  text('movetype', 'Move Type'),
  text('ratezone', 'Rate Zone'),
  text('quotenumber', 'Quote #'),
  text('csr', 'CSR'),
  text('whsecsr', 'Whse CSR'),
  text('dispatcher', 'Dispatcher'),
  text('commodity', 'Commodity'),
  text('size', 'Size'),
  text('equiptype', 'Equip Type'),
  text('isdrop', 'Drop'),
  text('otr', 'OTR'),
  text('hazmat', 'Hazmat'),
  text('hot', 'Hot'),
  text('billto', 'Bill-To'),
  text('billtoaddress', 'Bill-To Address'),
  text('billtocitystatezip', 'Bill-To City/State/Zip'),
  text('sagetag', 'Sage Tag'),
  text('customer', 'Customer'),
  text('customeraddress', 'Customer Address'),
  text('customercitystatezip', 'Customer City/State/Zip'),
  text('brokerforwarder', 'Broker/Forwarder'),
  text('pickup', 'Pickup'),
  text('pickupaddress', 'Pickup Address'),
  text('pickupcitystatezip', 'Pickup City/State/Zip'),
  text('delivery', 'Delivery'),
  text('deliveryaddress', 'Delivery Address'),
  text('deliverycitystatezip', 'Delivery City/State/Zip'),
  text('comments', 'Comments'),
  text('cmscode', 'CMS Code'),
  text('approvedby', 'Approved By'),
  text('approveddatetime', 'Approved'),
  text('deleted', 'Deleted'),
];
const TEMPLATE_VISIBLE = new Set([
  'pronumber', 'movetype', 'ratezone', 'quotenumber', 'csr', 'billto', 'sagetag',
  'customer', 'pickup', 'delivery', 'approvedby', 'approveddatetime',
]);

// ── View 2: Invoice line-item level (one row per line item) ────────────────
const LINEITEM_COLUMNS: ColumnDef<TemplateRow>[] = [
  proColumn,
  text('movetype', 'Move Type'),
  text('ratezone', 'Rate Zone'),
  text('quotenumber', 'Quote #'),
  text('csr', 'CSR'),
  text('whsecsr', 'Whse CSR'),
  text('dispatcher', 'Dispatcher'),
  text('commodity', 'Commodity'),
  text('size', 'Size'),
  text('equiptype', 'Equip Type'),
  text('isdrop', 'Drop'),
  text('otr', 'OTR'),
  text('hazmat', 'Hazmat'),
  text('hot', 'Hot'),
  text('billto', 'Bill-To'),
  text('sagetag', 'Sage Tag'),
  text('customer', 'Customer'),
  text('pickup', 'Pickup'),
  text('delivery', 'Delivery'),
  text('accessorialnum', 'Acc #'),
  text('invoicecomments', 'Invoice Comments'),
  num('linumber', 'Line #', 0),
  text('liitem', 'Item'),
  num('liqty', 'Qty', 2),
  num('lirate', 'Rate', 2),
  text('liratetype', 'Rate Type'),
  num('litotal', 'Total', 2),
  text('approvedby', 'Approved By'),
  text('approveddatetime', 'Approved'),
  text('deleted', 'Deleted'),
];
const LINEITEM_VISIBLE = new Set([
  'pronumber', 'billto', 'sagetag', 'customer', 'pickup', 'delivery',
  'linumber', 'liitem', 'liqty', 'lirate', 'liratetype', 'litotal',
]);

/** Columns not in the visible set default to hidden (toggle via the column menu). */
function hiddenDefaults(columns: ColumnDef<TemplateRow>[], visible: Set<string>): VisibilityState {
  const v: VisibilityState = {};
  for (const c of columns) {
    const id = c.id as string;
    if (!visible.has(id)) v[id] = false;
  }
  return v;
}

/**
 * Templates — Results grid, rendered with the shared TanTable. A segmented
 * Template | Line Items toggle (in headerExtra) switches the two views; two
 * checkboxes (View deleted / View unapproved) sit in the searchPrefix slot and
 * refetch server-side. Everything within the loaded set — sort / column-filter /
 * global-search / density / Excel export / pagination — is client-side. Row
 * click navigates to the template detail page.
 */
const TemplatesTable: React.FC<Props> = ({
  rows, capped, loading, error,
  view, onViewChange,
  status, onStatusChange,
  includesDeleted = false,
  statusApplied = true,
  onOpenTemplate,
  bulkActions,
  canEdit = false,
  onEditsApplied,
  fieldSelection,
}) => {
  // Marks a template viewed when its row is opened; the Template # column then
  // shows the green pill (+ ring on the most recently opened one), the same way
  // PROs highlight elsewhere. In the Line Items view every row of that template
  // turns green, since tracking is by template ID. No-op without a provider.
  const { markViewed } = useViewedPros();
  const isTemplate = view === 'template';
  const baseColumns = isTemplate ? TEMPLATE_COLUMNS : LINEITEM_COLUMNS;

  // Any orders-scope registry field can be added, resolved against the TEMPLATE
  // tables. In the Line Items view a template with several invoice lines is several
  // rows, so an added template-level column repeats down them — sibling rows, not a
  // parent with sub-rows, so no isSubRow.
  const { columns, availableFields } = useAdditionalFieldColumns<TemplateRow>(
    fieldSelection, { baseColumns, binding: 'templates' },
  );

  // ── inline editing ─────────────────────────────────────────────────────────
  // Which columns may be edited comes from the SERVER: the is_mass_editable flags
  // crossed with the template field policy. Empty until it loads (or if the call
  // fails), and an empty map means no affordance anywhere — a read-only grid beats
  // cells that fail on save.
  const [gridFields, setGridFields] = useState<GridFieldMap>({});
  const staged = useStagedEdits();
  const stagedKeys = useMemo(() => new Set(Object.keys(staged.edits)), [staged.edits]);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<
    { ok: number; failed: { pronumber: string; errors: string[] }[] } | null
  >(null);

  useEffect(() => {
    if (!canEdit) return;
    const ctrl = new AbortController();
    ordersService.changeAllGridFields(ctrl.signal, 'templates')
      .then((f) => { if (!ctrl.signal.aborted) setGridFields(f); })
      .catch(() => { /* no map, no editing — the grid stays read-only */ });
    return () => ctrl.abort();
  }, [canEdit]);

  // The Line Items view is one row per invoice line, so a template with three lines is
  // three rows — and every editable field here is keyed by pronumber, so an edit could
  // not be placed. Judged on the rows CURRENTLY VISIBLE, not everything fetched:
  // filtering down to one row per template genuinely resolves it, and the user should
  // get editing back without switching view.
  const [visiblePros, setVisiblePros] = useState<string[] | null>(null);
  const handleFilteredRows = useCallback((filtered: TemplateRow[]) => {
    const pros = filtered.map((r) => String(r.pronumber ?? '')).filter(Boolean);
    setVisiblePros((prev) => (
      prev && prev.length === pros.length && prev.every((p, i) => p === pros[i])
        ? prev
        : pros
    ));
  }, []);
  const dupePros = useMemo(
    // Before the grid has reported, judge on everything fetched — erring toward
    // read-only rather than briefly offering an edit that cannot be placed.
    () => duplicatedPros(visiblePros ? visiblePros.map((pronumber) => ({ pronumber })) : rows),
    [visiblePros, rows],
  );
  const templatesDisabled = dupePros.length > 0;

  const editEnabled = canEdit && Object.keys(gridFields).length > 0;

  // Editing is opt-in per column, as it is on Search Results: a grid people mostly
  // READ should not put an input under every cursor, and an accidental drag across a
  // live column is a much easier mistake than deliberately switching editing on.
  const [editingColumns, setEditingColumns] = useState<ReadonlySet<string>>(new Set());
  const toggleColumnEditing = useCallback((columnId: string) => {
    setEditingColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
        // Switching a column off ends its edit session, so what it staged goes with
        // it — a pending change behind a switch that reads "off", on a value the cell
        // no longer shows, is worse than losing the edit.
        const spec = specFor(columnId, gridFields);
        if (spec) {
          const prefix = keyPrefixForScope(spec.scope);
          staged.discardCells(
            (key, column) => column === spec.column && key.startsWith(prefix),
          );
        }
      } else {
        next.add(columnId);
      }
      return next;
    });
  }, [gridFields, staged]);

  // Asked of the COLUMN (the menu toggle + the drag affordance), so answered at its
  // most permissive: a State-2 column is offered whenever ANY visible template could
  // take it, and the per-cell gate below withholds the approved rows.
  const columnIsEditable = useCallback(
    (columnId: string) => !!templateGridEditMetaFor(columnId, gridFields, {
      isApproved: false, templatesDisabled,
    }),
    [gridFields, templatesDisabled],
  );

  // The same columns with the editable ones swapped for an in-place editor. Kept
  // separate from `columns` so the picker and the saved layouts keep seeing the plain
  // definitions — only what the grid renders changes.
  const editableColumns = useMemo<ColumnDef<TemplateRow>[]>(() => {
    if (!editEnabled) return columns;
    return columns.map((col) => {
      const id = (col as { id?: string; accessorKey?: string }).id
        ?? (col as { accessorKey?: string }).accessorKey;
      if (!id) return col;
      if (!columnIsEditable(id)) return col;
      const spec = specFor(id, gridFields)!;
      // Offered, but not live until the user turns this column on. The bulk-edit meta
      // rides along either way so the drag affordance follows the same switch.
      if (!editingColumns.has(id)) return col;
      return {
        ...col,
        meta: {
          ...((col as { meta?: object }).meta ?? {}),
          bulkEdit: templateGridEditMetaFor(id, gridFields, {
            isApproved: false, templatesDisabled,
          })!,
        },
        cell: ({ row }: { row: { original: TemplateRow } }) => {
          const data = row.original as unknown as Record<string, unknown>;
          // Per ROW: an approved template's State-2 cell is plain text beside an
          // unapproved one's editor.
          const meta = templateGridEditMetaFor(id, gridFields, {
            isApproved: templateRowIsApproved(data), templatesDisabled,
          });
          if (!meta) return <span className="text-gray-400">{String(data[id] ?? '')}</span>;
          const key = stagingKey(spec, data);
          return (
            <EditableCell
              value={key ? staged.effective(key, spec.column, data[id]) : data[id]}
              meta={meta}
              edited={!!key && staged.isEdited(key, spec.column)}
              disabled={saving || !key}
              onCommit={(next) => key && staged.stage(key, spec.column, next, data[id])}
            />
          );
        },
      } as ColumnDef<TemplateRow>;
    });
  }, [columns, editEnabled, columnIsEditable, gridFields, templatesDisabled,
      editingColumns, staged, saving]);

  // Drag-select a range in an editable column — stages it, same as typing in a cell.
  const handleBulkEdit = useCallback(
    (columnId: string, dragged: TemplateRow[], value: unknown) => {
      const spec = specFor(columnId, gridFields);
      if (!spec) return;
      const entries = draggableRowsFor(spec, dragged as unknown as Record<string, unknown>[])
        .map((data) => {
          const key = stagingKey(spec, data);
          // The stored value rides along so a drag over rows that already hold it
          // stages nothing for them.
          return key ? { recno: key, original: data[columnId] } : null;
        })
        .filter((e): e is { recno: string; original: unknown } => !!e);
      if (entries.length) staged.stageMany(entries, spec.column, value);
    },
    [gridFields, staged],
  );

  // Save every staged template in ONE request. Each runs the saverules engine in its
  // own transaction server-side, so a refused template leaves the rest applied and
  // comes back named — those stay staged for a retry.
  const handleSave = useCallback(async () => {
    if (!staged.changes.length || saving) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const { results } = await ordersService.changeAllGridEdit({
        ...buildPayload(staged.changes),
        submitNonce: `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      const failed = results
        .filter((r) => !r.applied)
        .map((r) => ({ pronumber: String(r.pronumber), errors: r.errors ?? [] }));
      setSaveResult({ ok: results.length - failed.length, failed });
      if (failed.length) {
        staged.keepOnly(keysForPros(Object.keys(staged.edits), failed.map((f) => f.pronumber)));
      } else {
        staged.discard();
      }
      onEditsApplied?.();
    } catch (err: any) {
      setSaveResult({
        ok: 0,
        failed: [{ pronumber: '', errors: [err?.response?.data?.detail || 'Save failed.'] }],
      });
    } finally {
      setSaving(false);
    }
  }, [staged, saving, onEditsApplied]);

  // Built from the BASE columns on purpose: an added column is one the user just
  // asked for, so it must not be hidden by the view's default-visibility rule.
  const defaultColumnVisibility = useMemo(
    () => hiddenDefaults(baseColumns, isTemplate ? TEMPLATE_VISIBLE : LINEITEM_VISIBLE),
    [baseColumns, isTemplate],
  );

  // Segmented Template | Line Items toggle, hosted in TanTable's headerExtra slot.
  const viewToggle = (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex items-center rounded-md border border-slate-500 overflow-hidden text-[0.6875rem] font-semibold"
    >
      {(['template', 'lineitem'] as const).map((mode) => {
        const active = view === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={active}
            onClick={() => onViewChange?.(mode)}
            className={[
              'px-2.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
              active ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-200 hover:bg-slate-600',
            ].join(' ')}
          >
            {mode === 'template' ? 'Template' : 'Line Items'}
          </button>
        );
      })}
    </div>
  );

  // The category picker: ONE of two mutually-exclusive server-side views, in the
  // same segmented idiom as the Template | Line Items switch above it.
  //
  // Replaces two additive toggles (View Deleted / View Unapproved) whose four
  // combinations included "everything" — 82,718 rows in prod, which is why the board
  // needed a 10k cap and why a capped load returned an arbitrary slice. Each category
  // is bounded by its own definition (~2k and ~12k), so both now load in full.
  //
  // Disabled while a custom search or filter is active: those span every status
  // server-side, so the picker would be claiming something untrue.
  const STATUS_LABELS: Record<TemplateStatus, string> = {
    approved: 'Active Approved',
    unapproved: 'Not Approved',
  };
  const statusPicker = (
    <div
      role="group"
      aria-label="Template status"
      title={statusApplied
        ? undefined
        : 'A custom search or filter defines its own scope, so this choice does not apply.'}
      className={`inline-flex items-center rounded-md border border-slate-500 overflow-hidden text-[0.6875rem] font-semibold${
        statusApplied ? '' : ' opacity-50'}`}
    >
      {(['approved', 'unapproved'] as const).map((s) => {
        const active = statusApplied && status === s;
        return (
          <button
            key={s}
            type="button"
            aria-pressed={active}
            disabled={!statusApplied}
            onClick={() => onStatusChange?.(s)}
            className={[
              'px-2.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
              statusApplied ? '' : 'cursor-default',
              active ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-200 hover:bg-slate-600',
            ].join(' ')}
          >
            {STATUS_LABELS[s]}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="templates-results-region">
      {/* Cap banner: the load is capped at the backend's RESULT_CAP. When capped,
          the grid shows only the first slice — narrow via Custom Search (the
          client-side sort/filter/search then operate on the loaded set). */}
      {capped && (
        <div
          role="status"
          className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2.5 text-[0.78125rem] text-amber-800 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-700"
        >
          Showing the first {rows.length.toLocaleString()} rows — narrow your search or
          filter to see the rest. (The two category views are never truncated.)
        </div>
      )}

      {/* Unsaved inline edits. Pinned above the results and deliberately loud:
          staging is what makes a mis-drag recoverable, but only if the user can see
          that nothing has been written yet. */}
      {staged.recordCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-semibold">
            {staged.cellCount} unsaved change{staged.cellCount === 1 ? '' : 's'} on{' '}
            {staged.recordCount} template{staged.recordCount === 1 ? '' : 's'}
          </span>
          <span className="text-xs text-amber-800 dark:text-amber-300">
            Nothing is written until you save.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={staged.discard}
              disabled={saving}
              className="rounded border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:bg-gray-900 dark:text-amber-200"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded border border-blue-600 bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving
                ? 'Saving…'
                : `Save ${staged.recordCount} template${staged.recordCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* Outcome of the last save. Failures name the templates and stay staged — a
          refusal here is usually "this one is approved", which the user fixes by
          unapproving it and saving again. */}
      {saveResult && (
        <div
          className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
            saveResult.failed.length
              ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-700/50 dark:bg-red-900/20 dark:text-red-200'
              : 'border-green-300 bg-green-50 text-green-800 dark:border-green-700/50 dark:bg-green-900/20 dark:text-green-200'
          }`}
        >
          <span>
            {saveResult.ok > 0
              && `${saveResult.ok} template${saveResult.ok === 1 ? '' : 's'} updated. `}
            {saveResult.failed.slice(0, 3).map((f) => (
              <span key={f.pronumber} className="block">
                {f.pronumber ? `${f.pronumber}: ` : ''}{f.errors.join(' · ') || 'Failed.'}
              </span>
            ))}
            {saveResult.failed.length > 3 && (
              <span className="block">…and {saveResult.failed.length - 3} more.</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setSaveResult(null)}
            className="shrink-0 font-medium"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* One template on several rows makes inline editing ambiguous — every editable
          field here is keyed by pronumber. The Line Items view is where this happens:
          one row per invoice line. */}
      {editEnabled && templatesDisabled && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          Fields are read-only: {dupePros.length} template{dupePros.length === 1 ? '' : 's'}{' '}
          appear{dupePros.length === 1 ? 's' : ''} on more than one row, so an edit could
          not be matched to a single template. Switch to the Template view, or filter
          these results down to one row per template.
        </div>
      )}

      {/* Where the deleted templates went. 68,653 of the 82,718 in prod are
          soft-deleted, and NEITHER category view includes them — so without this the
          board silently omits 83% of the table and offers no clue where to look. Once
          they are in scope — a custom search, or a filter with Include Deleted — the
          note flips to saying so rather than disappearing (which would read as the
          opposite). */}
      <div
        role="note"
        className="flex items-start gap-2 rounded-lg border border-slate-200 bg-panel px-4 py-2 text-[0.75rem] text-slate-600 dark:border-gray-700 dark:text-slate-300"
      >
        <InformationCircleIcon className="mt-px h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
        {includesDeleted ? (
          <span>
            <strong className="font-semibold">Deleted templates are included</strong> in
            these results.
          </span>
        ) : (
          <span>
            <strong className="font-semibold">Deleted templates are not shown.</strong>{' '}
            To see them, use <strong className="font-semibold">Custom Search</strong> (by
            Template #, Bill To, Delivery, Quote #… — it always spans every status), or{' '}
            <strong className="font-semibold">Custom Filter</strong> with{' '}
            <strong className="font-semibold">Include Deleted</strong> ticked.
          </span>
        )}
      </div>

      {/* Distinct `key` per view (template ↔ lineitem) forces a fresh TanTable mount
          on every toggle, so each view loads + saves its OWN table_<settingsKey>
          settings (templates-template-grid vs templates-lineitem-grid) and the two
          never cross-write each other's saved column layout. TanTable loads its
          settings once per mount and never re-reads on a settingsKey change, so a
          shared instance would keep the other view's layout and its debounced
          autosave would write it into the wrong key — the same class of bug fixed on
          the Quotes Accounts↔Quotes toggle. */}
      <TanTable<TemplateRow>
        key={view}
        data={rows}
        columns={editableColumns}
        availableFields={availableFields}
        additionalFields={fieldSelection.additionalFields}
        onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
        cardTitle={isTemplate ? 'Template View' : 'Invoice Line Item View'}
        headerExtra={viewToggle}
        searchPrefix={statusPicker}
        trackViewedColumnId="pronumber"
        onRowClick={(r) => { markViewed(String(r.pronumber ?? '')); onOpenTemplate?.(r); }}
        bulkActions={bulkActions}
        {...(editEnabled
          ? {
              onBulkEdit: handleBulkEdit,
              canEditColumn: columnIsEditable,
              isColumnEditing: (columnId: string) => editingColumns.has(columnId),
              onToggleColumnEditing: toggleColumnEditing,
            }
          : {})}
        {...(canEdit ? { onFilteredRowsChange: handleFilteredRows } : {})}
        // Soft-deleted templates carry the same red tint as every other grid in the
        // app (search results, Locations, Repair Issues) — one shared helper, so a
        // deleted template reads identically wherever it surfaces. In the line-item
        // view every row of a deleted template tints, which is correct: the deletion
        // is at template level.
        getRowClassName={(row, m) => (
          // Deleted still wins — it is the more important thing to notice about a row.
          // Otherwise a row carrying an unsaved edit is tinted amber: a single amber
          // cell is easy to lose in a grid that scrolls sideways, and the row it
          // belongs to may be the only part still on screen.
          row.deleted === 'Y'
            ? deletedRowClass(true, m.zebra)
            : rowHasStagedEdit(row as unknown as Record<string, unknown>, stagedKeys)
              ? 'bg-amber-50 dark:bg-amber-900/20'
              : deletedRowClass(false, m.zebra)
        )}
        // ONE id shape for both views, deliberately not `isTemplate ? ... : ...`.
        //
        // Switching view flips `view` (and so getRowId) immediately, but `rows` only
        // catches up when the refetch resolves — so there is a render where LINE-ITEM
        // data is keyed by the template view's rule. Keyed on pronumber alone, every
        // template with more than one line item produced DUPLICATE React keys in that
        // window; the duplicate-keyed rows were then not reconciled away when the
        // template data arrived, leaving orphaned absolutely-positioned <tr>s painted
        // over each other and over the real rows. That is the reported "4 rows in the
        // count, 6 on screen, one written over itself".
        //
        // accessorialnum / linumber are simply absent on a template row, so this
        // yields "T81706--" there (one per template, stable) and
        // "T81706-0-1" per line item — unique in either shape, and unique even when
        // the two are mid-swap.
        getRowId={(r) => `${r.pronumber}-${r.accessorialnum ?? ''}-${r.linumber ?? ''}`}
        loading={loading}
        errorMessage={error}
        settingsKey={isTemplate ? 'templates-template-grid' : 'templates-lineitem-grid'}
        // The per-view key above remounts this grid on every toggle, so apply the
        // localStorage-cached layout pre-paint to avoid flashing the default column
        // order/width/visibility before the async settings load resolves.
        seedLayoutFromCache
        defaultColumnVisibility={defaultColumnVisibility}
        searchPlaceholder="Filter Results..."
        enableColumnFilters
        enableSearch
        enableShowHideCols
        enableExcelExport
        enableReset
        // Show ALL rows (virtualized) — no pagination, no rows-per-page selector.
        enablePagination={false}
        enableRowSelection={false}
      />
    </div>
  );
};

export default TemplatesTable;
